import { DownOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CompareObject from "./CompareObject";
import { useParseObject } from "./ParseObject";
import { useApis } from "./Apis";
import favicon from "./favicon.svg";

const PAGE_TABS = [
    { key: "parse-object", label: "Parse Object" },
    { key: "apis", label: "APIs" },
    { key: "compare-object", label: "Compare Object" },
] as const;
type PageTab = typeof PAGE_TABS[number]["key"];
const PAGE_TAB_STORAGE_KEY = "parse-object.active-tab";
const isPageTab = (value: string | null): value is PageTab => PAGE_TABS.some(tab => tab.key === value);
const readSavedPageTab = (): PageTab => {
    try {
        const saved = localStorage.getItem(PAGE_TAB_STORAGE_KEY);
        return isPageTab(saved) ? saved : "parse-object";
    } catch {
        return "parse-object";
    }
};

const ParseObjectPage = () => {
    useEffect(() => {
        const existingIcon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        const icon = existingIcon ?? document.createElement("link");
        const previousHref = icon.getAttribute("href");
        icon.rel = "icon";
        icon.href = favicon;
        if (!existingIcon) document.head.appendChild(icon);
        return () => {
            if (!existingIcon) icon.remove();
            else if (previousHref !== null) icon.setAttribute("href", previousHref);
            else icon.removeAttribute("href");
        };
    }, []);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedPageTab = searchParams.get("tab");
    const activePageTab = isPageTab(requestedPageTab) ? requestedPageTab : readSavedPageTab();
    const changePageTab = (tab: PageTab) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            next.set("tab", tab);
            return next;
        });
    };
    useEffect(() => {
        try {
            localStorage.setItem(PAGE_TAB_STORAGE_KEY, activePageTab);
        } catch {
            // Tabs still work when browser storage is unavailable.
        }
        if (requestedPageTab !== activePageTab) {
            setSearchParams(previous => {
                const next = new URLSearchParams(previous);
                next.set("tab", activePageTab);
                return next;
            }, { replace: true });
        }
    }, [activePageTab, requestedPageTab, setSearchParams]);
    const parser = useParseObject();
    const apis = useApis({ activePageTab, changePageTab });

    return (
        <main className={`[&_button:not(:disabled)]:cursor-pointer h-screen overflow-auto bg-[#181a1f] text-slate-200 ![scrollbar-color:#525660_#202329] [&_*]:![scrollbar-color:#525660_#202329] [&::-webkit-scrollbar-track]:!bg-[#202329] [&_*::-webkit-scrollbar-track]:!bg-[#202329] [&::-webkit-scrollbar-thumb]:!bg-[#525660] [&_*::-webkit-scrollbar-thumb]:!bg-[#525660] [&::-webkit-scrollbar-corner]:!bg-[#202329] [&_*::-webkit-scrollbar-corner]:!bg-[#202329] lg:overflow-hidden ${(activePageTab !== "apis" || (!apis.selectedApiEndpoint && !apis.apiResponseInfo)) ? "max-sm:h-dvh max-sm:overflow-hidden" : ""}`}>
            <div className={`mx-auto flex min-h-screen max-w-[1900px] flex-col p-3 sm:px-8 sm:py-6 lg:h-full lg:min-h-0 lg:px-12 ${activePageTab !== "apis" ? "max-sm:h-full max-sm:min-h-0 pb-[calc(3rem+12px+env(safe-area-inset-bottom))] sm:pb-6" : (!apis.selectedApiEndpoint && !apis.apiResponseInfo) ? "max-sm:h-full max-sm:min-h-0" : ""}`}>
                <div className="relative mb-4 flex shrink-0 items-center gap-3 sm:flex-wrap">
                    <div>
                        <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                            Developer tool
                        </div>
                        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
                            Parse Object
                        </h1>
                    </div>
                    <div className="ml-auto mb-[-2px] min-w-0 max-w-[140px] flex-1 self-end sm:hidden">
                        <Dropdown
                            trigger={["click"]}
                            placement="bottomRight"
                            menu={{
                                selectable: true,
                                selectedKeys: [activePageTab],
                                items: PAGE_TABS.map(tab => ({ key: tab.key, label: tab.label })),
                                onClick: ({ key }) => { if (isPageTab(key)) changePageTab(key); },
                            }}
                            classNames={{ root: "[--bgModal:#202329] [--colorSecond:#312e51] [--colorHover:#334155] [--fontActive:#e2e8f0] [&_.ant-dropdown-menu]:!rounded-lg [&_.ant-dropdown-menu]:!border [&_.ant-dropdown-menu]:!border-solid [&_.ant-dropdown-menu]:!border-slate-700 [&_.ant-dropdown-menu]:!bg-[#202329] [&_.ant-dropdown-menu-item]:!rounded-md [&_.ant-dropdown-menu-item]:!text-slate-300 [&_.ant-dropdown-menu-title-content]:!text-xs [&_.ant-dropdown-menu-title-content]:!font-semibold [&_.ant-dropdown-menu-item:hover]:!bg-slate-700 [&_.ant-dropdown-menu-item-selected]:!bg-indigo-500/20 [&_.ant-dropdown-menu-item-selected]:!text-indigo-300" }}
                        >
                            <button aria-label="Select developer tool" className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-[#373b44] bg-[#202329] px-2.5 text-xs font-semibold text-indigo-300 transition-colors hover:bg-slate-800" type="button">
                                <span className="truncate">{PAGE_TABS.find(tab => tab.key === activePageTab)?.label}</span>
                                <DownOutlined className="shrink-0 text-[10px] text-slate-400" />
                            </button>
                        </Dropdown>
                    </div>
                    <nav aria-label="Developer tools" className="relative mx-auto hidden grid-cols-3 sm:grid rounded-lg border border-[#373b44] bg-[#202329] p-1 lg:absolute lg:left-1/2 lg:-translate-x-1/2">
                        <span
                            aria-hidden="true"
                            className={`pointer-events-none absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/3)] rounded-md bg-slate-800 ring-1 ring-slate-700 transition-transform duration-300 ease-out motion-reduce:transition-none ${activePageTab === "apis" ? "translate-x-full" : activePageTab === "compare-object" ? "translate-x-[200%]" : "translate-x-0"}`}
                        />
                        {PAGE_TABS.map(tab => (
                            <button
                                aria-current={activePageTab === tab.key ? "page" : undefined}
                                className={`relative z-10 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-200 motion-reduce:transition-none ${activePageTab === tab.key ? "text-indigo-300" : "text-slate-400 hover:text-slate-200"}`}
                                key={tab.key}
                                onClick={() => changePageTab(tab.key)}
                                type="button"
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className={activePageTab === "compare-object" ? "flex min-h-0 flex-1 flex-col" : "hidden"}><CompareObject /></div>
                {parser.render(activePageTab)}
                {apis.render()}
            </div>
        </main>
    );
};

export default ParseObjectPage;
